/**
 * Month-snap conversions for the Segment Builder's date RangeControl slider.
 *
 * The wire value for a date range is a YYYYMMDD integer ordinal (e.g. 20210315),
 * the projection's date axis — that never changes. The slider, however, steps by
 * MONTH: it maps the ordinal domain onto a dense `monthIndex` axis
 * (`year * 12 + (month - 1)`) so a Mantine RangeSlider can move one month per tick,
 * then converts a monthIndex back to an ordinal on emit.
 *
 * Convention (inclusive whole-month range):
 *   - the MIN handle → the FIRST day of its month (day = 01)
 *   - the MAX handle → the LAST day of its month (28/29/30/31 as the month dictates)
 * so a Mar 2021 – Sep 2023 selection is `[20210301, 20230930]` and the range covers
 * every day of both boundary months.
 *
 * These are PURE and UNIT-TESTED (month-snap.test.ts): the count, the loan list, and
 * the curves all read the emitted ordinals, so any drift here silently disagrees with
 * what the user sees on the slider.
 */

/** Days in a month, honoring leap years (month is 1-based). */
export function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this month; JS Date months are 0-based.
  return new Date(year, month, 0).getDate();
}

/** YYYYMMDD ordinal → dense month index (`year * 12 + (month - 1)`). Day is ignored. */
export function ordinalToMonthIndex(ordinal: number): number {
  const year = Math.floor(ordinal / 10000);
  const month = Math.floor((ordinal % 10000) / 100);
  return year * 12 + (month - 1);
}

/** Month index → the YYYYMMDD ordinal of the FIRST day of that month (min handle). */
export function monthIndexToStartOrdinal(monthIndex: number): number {
  const year = Math.floor(monthIndex / 12);
  const month = (monthIndex % 12) + 1;
  return year * 10000 + month * 100 + 1;
}

/** Month index → the YYYYMMDD ordinal of the LAST day of that month (max handle). */
export function monthIndexToEndOrdinal(monthIndex: number): number {
  const year = Math.floor(monthIndex / 12);
  const month = (monthIndex % 12) + 1;
  return year * 10000 + month * 100 + daysInMonth(year, month);
}

/** Month name + year for a slider label, e.g. `202103` → "Mar 2021". */
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Month index → a compact "Mon YYYY" label (e.g. "Mar 2021"). */
export function monthIndexToLabel(monthIndex: number): string {
  const year = Math.floor(monthIndex / 12);
  const month = monthIndex % 12; // 0-based into MONTH_ABBR
  return `${MONTH_ABBR[month]} ${year}`;
}
